import React, { useEffect, useState } from 'react';
import { RangePickerProps } from './types';
import { Box } from '@mui/material';
import moment from 'moment';
import { TimePicker, Typography, ConfigProvider } from 'antd';
import { Button } from '@alifd/next';
import locale from 'antd/es/locale/zh_TW';

const RangePicker: React.FC<RangePickerProps> = ({ ranges, setRanges }) => {
  const [timeStart, setTimeStart] = useState<moment.Moment>();
  const [timeEnd, setTimeEnd] = useState<moment.Moment>();
  const [status, setStatus] = useState<'error' | 'warning'>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [pickerIsOpen, setPickerIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (
      timeEnd &&
      timeEnd.format('HH:mm') === '00:00' &&
      !timeEnd.isSame(moment('24:00', 'HH:mm'))
    ) {
      setTimeEnd(timeEnd.add(1, 'days'));
    }
  }, [timeEnd]);

  useEffect(() => {
    if (timeStart && timeEnd) {
      if (timeStart.isSameOrAfter(timeEnd)) {
        setTimeEnd(timeStart);
        setErrorMessage('開始時間不能大於等於結束時間');
        setStatus('error');
      }
      ranges.forEach(([start, end]) => {
        const startMoment = moment(start, 'HH:mm');
        const endMoment = moment(end, 'HH:mm');
        if (
          timeStart.isBetween(startMoment, endMoment, 'minutes', '[]') ||
          timeEnd.isBetween(startMoment, endMoment, 'minutes', '[]') ||
          startMoment.isBetween(timeStart, timeEnd, 'minutes', '[]') ||
          endMoment.isBetween(timeStart, timeEnd, 'minutes', '[]')
        ) {
          setErrorMessage('時間段不能重疊');
          setStatus('error');
        }
      });
    }
    return () => {
      setStatus(undefined);
    };
  }, [timeStart, timeEnd, ranges]);

  const handleClick = () => {
    if (timeStart && timeEnd) {
      if (timeStart.isSameOrAfter(timeEnd)) {
        setErrorMessage('開始時間不能大於等於結束時間');
        setStatus('error');
      } else {
        const setNewRanges = (newEndTime: string) => {
          setRanges((prev) => {
            let newRanges: [string, string][] = [
              ...prev,
              [timeStart.format('HH:mm'), newEndTime],
            ];
            newRanges = newRanges.sort((a, b) => a[0].localeCompare(b[0]));
            return newRanges;
          });
        };

        if (timeEnd.format('HH:mm') === '00:00') {
          setNewRanges('24:00');
        } else {
          setNewRanges(timeEnd.format('HH:mm'));
        }

        setTimeStart(undefined);
        setTimeEnd(undefined);
      }
    }
  };

  return (
    <ConfigProvider locale={locale}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
          }}
        >
          <TimePicker
            minuteStep={30}
            onOpenChange={(open) => setPickerIsOpen(open)}
            value={timeStart}
            allowClear={false}
            format="HH:mm"
            status={status}
            onChange={(time) => {
              if (time) {
                setTimeStart(time);
              }
            }}
          />
          <TimePicker
            minuteStep={30}
            onOpenChange={(open) => setPickerIsOpen(open)}
            value={timeEnd}
            allowClear={false}
            format="HH:mm"
            status={status}
            onChange={(time) => {
              if (time) {
                setTimeEnd(time);
              }
            }}
          />
          <Button
            disabled={!!status || pickerIsOpen || !timeStart || !timeEnd}
            onClick={handleClick}
          >
            新增
          </Button>
        </Box>
        {status && (
          <Typography.Text type="danger">{errorMessage}</Typography.Text>
        )}
      </Box>
    </ConfigProvider>
  );
};

export default RangePicker;
